import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Menu, MenuItem, Typography, Box } from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useAuthStore } from '@/stores';
import { authApi } from '@/features/auth';

const languages = [
  { code: 'en', nativeLabel: 'English' },
  { code: 'zh', nativeLabel: '中文' },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
    const { isAuthenticated, patchSettings } = useAuthStore.getState();
    if (isAuthenticated()) {
      patchSettings({ lang: languageCode });
      await authApi.updateSettings({ lang: languageCode }).catch(() => {});
    }
    handleClose();
    // Influence 分析結果依賴 lang 參數向後端請求，需 reload 重新 fetch
    if (window.location.pathname.startsWith('/social-analysis')) {
      window.location.reload();
    }
  };

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          color: 'text.primary',
          display: 'flex',
          gap: 0.5,
          px: 1,
        }}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <LanguageIcon />
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {currentLanguage.nativeLabel}
        </Typography>
      </IconButton>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === i18n.language}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2">{language.nativeLabel}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
